import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// Alle möglichen Keys, die du evtl. beim Login speicherst
const KEYS = [
    "token", "access_token", "refresh_token",
    "authUser", "user", "session"
];

// -----------------------------------------------------------------------------
// Logout-Handling
// -----------------------------------------------------------------------------
export async function logoutUser({ callBackend = true } = {}) {
    try {
        if (callBackend && API_BASE) {
            // optional: Session-Cookie invalidieren (falls du Cookie-Auth nutzt)
            await axios.post(`${API_BASE}/logout`, {}, {
                withCredentials: true,
                timeout: 8000
            }).catch(() => {});
        }
    } finally {
        // Clientseitig aufräumen
        KEYS.forEach((k) => localStorage.removeItem(k));
        KEYS.forEach((k) => sessionStorage.removeItem(k));

        // Axios-Auth-Header entfernen, falls gesetzt
        try { delete axios.defaults.headers.common["Authorization"]; } catch (_) {}
    }
}
// -----------------------------------------------------------------------------
// User & Rollen-Handling (robust, inkl. JWT-Fallback)
// -----------------------------------------------------------------------------

function base64UrlDecode(input) {
    try {
        const pad = (str) => str + "===".slice((str.length + 3) % 4);
        const b64 = pad(input.replace(/-/g, "+").replace(/_/g, "/"));
        // atob ist im Browser verfügbar
        return decodeURIComponent(
            atob(b64)
                .split("")
                .map(c => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
                .join("")
        );
    } catch {
        return null;
    }
}

function parseJwt(token) {
    try {
        const parts = token.split(".");
        if (parts.length !== 3) return null;
        const payloadStr = base64UrlDecode(parts[1]);
        return payloadStr ? JSON.parse(payloadStr) : null;
    } catch {
        return null;
    }
}

/**
 * Versucht, den gespeicherten Nutzer zu laden (egal ob unter "user", "authUser",
 * ... und egal ob String/Objekt). Gibt ein Objekt zurück (oder null).
 */
export function getCurrentUser() {
    const keysTry = ["user", "authUser", "session", "auth_user"];
    for (const k of keysTry) {
        const raw =
            localStorage.getItem(k) ||
            sessionStorage.getItem(k);
        if (!raw) continue;
        try {
            const obj = typeof raw === "string" ? JSON.parse(raw) : raw;
            if (obj && typeof obj === "object") return obj;
        } catch {
            // ignorieren
        }
    }
    return null;
}

/**
 * Extrahiert Rollen aus verschiedenen Strukturen & Tokens und normalisiert sie.
 * Rückgabe: Array aus lowercase-Strings, z. B. ["admin","editor"]
 */
export function getUserRoles(userLike) {
    const roles = new Set();

    const pushAny = (v) => {
        if (!v) return;
        if (Array.isArray(v)) v.forEach(pushAny);
        else if (typeof v === "string") {
            // Split bei Komma/Leerzeichen; trim & lowercase
            v.split(/[, ]+/).forEach(s => {
                const t = (s || "").trim().toLowerCase();
                if (t) roles.add(t);
            });
        } else if (typeof v === "object") {
            // falls z. B. [{name:'ADMIN'}] oder [{authority:'ROLE_ADMIN'}]
            if ("name" in v && typeof v.name === "string") roles.add(v.name.toLowerCase());
            if ("authority" in v && typeof v.authority === "string") roles.add(v.authority.toLowerCase());
            if ("role" in v && typeof v.role === "string") roles.add(v.role.toLowerCase());
            if ("roles" in v) pushAny(v.roles);
        }
    };

    // 1) Häufige Felder
    if (userLike && typeof userLike === "object") {
        const candidates = [
            userLike.roles,
            userLike.role,
            userLike.scopes,
            userLike.permissions,
            userLike.authorities,
            userLike.data?.roles,
            userLike.user?.roles,
        ];
        candidates.forEach(pushAny);
    }

    // 2) JWT-Fallbacks (access_token, token)
    const tokenKeys = ["access_token", "token", "id_token"];
    for (const tk of tokenKeys) {
        const token =
            localStorage.getItem(tk) ||
            sessionStorage.getItem(tk);
        if (!token) continue;
        const payload = parseJwt(token);
        if (!payload) continue;

        // gängige Claims durchprobieren
        pushAny(payload.roles);
        pushAny(payload.role);
        pushAny(payload.permissions);
        pushAny(payload.scopes);
        // OpenID/OAuth2 "scope" string
        if (typeof payload.scope === "string") pushAny(payload.scope);
    }

    // 3) Auch in "authUser" JSON mit JWTs?
    const authRaw =
        localStorage.getItem("authUser") ||
        sessionStorage.getItem("authUser");
    if (authRaw) {
        try {
            const obj = JSON.parse(authRaw);
            if (obj?.access_token) {
                const payload = parseJwt(obj.access_token);
                if (payload) {
                    pushAny(payload.roles);
                    pushAny(payload.role);
                    pushAny(payload.permissions);
                    pushAny(payload.scopes);
                    if (typeof payload.scope === "string") pushAny(payload.scope);
                }
            }
            // evtl. sind Rollen auch direkt drin:
            pushAny(obj.roles);
            pushAny(obj.role);
        } catch {
            // ignore
        }
    }

    // 4) Rolle mit Prefix "role_" normalisieren (optional)
    const normalized = [...roles].map(r => r.replace(/^role[_:-]?/, "").toLowerCase());

    return Array.from(new Set(normalized));
}

/**
 * Prüft, ob User die Rolle besitzt (case-insensitiv).
 */
export function hasRole(userLike, role) {
    if (!role) return true;
    const r = (role || "").toLowerCase();
    const set = new Set(getUserRoles(userLike));
    return set.has(r);
}

/**
 * true, wenn keine Einschränkung ODER mind. eine der Rollen passt.
 */
export function hasAnyRole(userLike, roles) {
    if (!roles || roles.length === 0) return true;
    const have = new Set(getUserRoles(userLike));
    return roles.some(r => have.has((r || "").toLowerCase()));
}
